/**
 * components/landing/platform/sections/WhiteLabelInfra.jsx — §3.6.
 *
 * The 9-step white-label infrastructure walkthrough.  Compact and
 * scannable — each step is a clickable card; the active card shows
 * extra copy.  Complementary to the Phase 2.3 Customer Journey which
 * focuses on the customer-side experience.
 */

import React, { useState } from 'react'
import clsx from 'clsx'
import {
  ServerCog, Palette, Globe, CreditCard, UsersRound, ShieldCheck,
  ToggleRight, Rocket, RefreshCw, Sparkles, ArrowRight,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { WL_INFRA } from '../data'

const ICONS = { ServerCog, Palette, Globe, CreditCard, UsersRound, ShieldCheck, ToggleRight, Rocket, RefreshCw }
const TONE = {
  orange: { hex: '#FFC107', rgb: '243,112,33' },
  green:  { hex: '#22c55e', rgb: '34,197,94' },
  blue:   { hex: '#3b82f6', rgb: '59,130,246' },
  purple: { hex: '#a855f7', rgb: '168,85,247' },
  cyan:   { hex: '#22d3ee', rgb: '34,211,238' },
}

export default function WhiteLabelInfra() {
  const [openId, setOpenId] = useState(WL_INFRA[0]?.id)
  return (
    <section className="relative py-24 md:py-28">
      <Container size="wide" className="flex flex-col gap-12">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>White-Label Infrastructure</Eyebrow>
          <SectionHeading
            align="center"
            title={<>The full <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">launch stack</span>, ready in a week.</>}
            subtitle="Behind every white-label Kalki Ai Cloud tenant sits the same nine-layer infrastructure. You configure the surface — we operate the depth."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {WL_INFRA.map((s) => {
            const tone = TONE[s.tone] || TONE.orange
            const Icon = ICONS[s.iconKey] || Sparkles
            const open = openId === s.id
            return (
              <button
                key={s.id}
                onClick={() => setOpenId(open ? null : s.id)}
                className={clsx(
                  'group relative text-left rounded-2xl border p-5 transition-all overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/60',
                  open
                    ? 'border-white/20 bg-gradient-to-b from-white/[0.05] to-white/[0.01] shadow-[0_20px_60px_-30px_rgba(0,0,0,0.9)]'
                    : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.03]',
                )}
              >
                {open && (
                  <div className="pointer-events-none absolute inset-0 opacity-70"
                    style={{ background: `radial-gradient(70% 45% at 20% 0%, rgba(${tone.rgb},0.12), transparent 70%)` }} />
                )}
                <div className="relative flex items-start gap-3">
                  <span
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}
                  >
                    <Icon strokeWidth={1.8} style={{ width: 16, height: 16 }} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-semibold text-white leading-tight">{s.label}</div>
                    <p className={clsx('text-[13px] leading-relaxed mt-1', open ? 'text-zinc-200' : 'text-zinc-400')}>
                      {s.summary}
                    </p>
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        <div className="mt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.02] px-5 md:px-6 py-4 backdrop-blur-xl">
          <p className="text-sm text-zinc-300">
            Nine layers, one button. Once the infrastructure is composed, going live is a single click — zero downtime, zero support ticket.
          </p>
          <a
            href="/white-label"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-gold hover:brightness-125"
          >
            White-label overview <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </Container>
    </section>
  )
}
